import { useEffect, useMemo, useRef } from "react";
import { useAtom, useAtomValue } from "jotai";
import {
  BudgetChatMessageProps,
  BudgetChatMessageType,
  tabsIndexAtom,
  currentStyleAtom,
  generateLoadingPayload,
  generateTopicContentPayload,
  generateTopicTitlePayload,
  interestedTopicsAtom,
  messagesAtom,
  selectedStyleAtom,
  viewedTopicsAtom,
  getExistingTopicMessageId,
} from "../constants/budget-atom";
import BudgetChatHeader from "../ui/budget-chat-header";
import BudgetChatLoadingPlaceholder from "../ui/budget-chat-placeholder";
import BudgetChatQuery from "../ui/budget-chat-query";
import BudgetChatResponse from "../ui/budget-chat-response";
import { motion } from "framer-motion";

const BudgetChat: React.FC = () => {
  const interestedTopics = useAtomValue(interestedTopicsAtom);
  const tabsIndex = useAtomValue(tabsIndexAtom);
  const [messages, setMessages] = useAtom(messagesAtom);
  const globalSelectedStyle = useAtomValue(selectedStyleAtom);
  /// Local value to check if the style has been updated
  const [localSelectedStyle, setCurrentStyle] = useAtom(currentStyleAtom);
  const [viewedTopics, setViewedTopics] = useAtom(viewedTopicsAtom);

  // Use string keys to match messageId type consistently
  const messageRefs = useRef<Map<string, HTMLElement>>(new Map());

  const initialTopics = useMemo(() => {
    if (!interestedTopics?.length || !globalSelectedStyle) return null;
    return generateTopicTitlePayload(interestedTopics, 0, globalSelectedStyle);
  }, [interestedTopics, globalSelectedStyle]);

  /// Given a [payload], will update the payload of the message with the corresponding `messageId`
  const updateChatMessageContent = (payload: BudgetChatMessageProps) => {
    if (!payload.messageId || !payload.content) return;

    setMessages((prev) => {
      const temp = [...prev];
      const index = temp.findIndex(
        (m) =>
          m.messageId === payload.messageId &&
          m.type != BudgetChatMessageType.QUERY
      );

      if (index !== -1) {
        temp[index] = { ...payload };
      }
      return temp;
    });
  };

  /// Initialise topic and contents
  /// Starts by initialising the topics and loading placeholder, the contents are then updated
  /// after a delay
  useEffect(() => {
    if (tabsIndex !== 2 || !interestedTopics?.length || !initialTopics) return;

    const hasUpdatedStyle = globalSelectedStyle !== localSelectedStyle;
    const firstMessage = messages.length ? messages.at(0)["content"] : [];
    const currentTopics = JSON.stringify(firstMessage);
    const queryTopics = JSON.stringify(initialTopics.content);

    if (hasUpdatedStyle || currentTopics !== queryTopics) {
      if (hasUpdatedStyle) {
        setCurrentStyle(globalSelectedStyle);
      }
      onTopicSelected(interestedTopics, true);
    }
  }, [
    tabsIndex,
    interestedTopics,
    globalSelectedStyle,
    initialTopics,
    localSelectedStyle,
  ]);

  /// Reusable function that is called whenever a topic is selected
  /// Called either during the initialization of the page, or when a topic from the suggestions
  /// is tapped on
  ///
  /// Will generate query and response payload, then update accordingly
  ///
  /// [isInit] determines if this is called during the initialization of the page
  /// When [isInit] is `true`, the value of viewedTopics and messages will be different
  function onTopicSelected(topicKeys: string[], isInit?: boolean) {
    if (!isInit) {
      const existingMessageId = getExistingTopicMessageId(topicKeys, messages);
      if (existingMessageId) {
        setTimeout(() => scrollToMessage(existingMessageId), 100);
        return;
      }
    }

    const uniqueIds = topicKeys.filter((id) => !viewedTopics.includes(id));
    if (!isInit && uniqueIds.length === 0) return;
    if (isInit && !topicKeys.length) return;

    setViewedTopics(isInit ? topicKeys : (prev) => [...prev, ...uniqueIds]);

    // Determine the index for the new messages
    const topicQueryIndex = isInit ? 0 : messages.length;
    const topicResponseIndex = topicQueryIndex + 1;

    /// Build payloads
    const topicTitlePayload = generateTopicTitlePayload(
      topicKeys,
      topicQueryIndex,
      globalSelectedStyle
    );
    const placeholderMessage = generateLoadingPayload(topicResponseIndex);

    setMessages(
      isInit
        ? [topicTitlePayload, placeholderMessage]
        : (prev) => [...prev, topicTitlePayload, placeholderMessage]
    );

    const topicContent = generateTopicContentPayload(
      topicKeys,
      topicResponseIndex,
      globalSelectedStyle
    );

    /// Update placeholder after 1.5s
    setTimeout(() => {
      updateChatMessageContent(topicContent);
    }, 1500);

    if (!isInit) {
      setTimeout(() => scrollToMessage(topicTitlePayload.messageId), 200);
    }
  }

  /// Registers a ref to the [messageRef], used in [scrollToMessage]
  const registerMessageRef = (
    messageId: string | number,
    element: HTMLElement | null
  ) => {
    // Convert to string for consistent key handling
    const messageKey = String(messageId);

    if (element) {
      messageRefs.current.set(messageKey, element);
    } else {
      messageRefs.current.delete(messageKey);
    }
  };

  /// Scrolls to a message of a given [messageId]
  const scrollToMessage = (messageId: string | number) => {
    const messageKey = String(messageId);

    const element = messageRefs.current.get(messageKey);
    if (!element) {
      return;
    }

    const offset = 48;
    const top = element.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top, behavior: "smooth" });
  };

  return (
    <div className="mx-8 mb-16">
      <div className="mt-2">
        <BudgetChatHeader />
      </div>
      <div className="w-min-full mt-4 flex flex-col space-y-4">
        {messages.map((message, index) => {
          const isQuery =
            message.type === BudgetChatMessageType.QUERY ||
            message.type === BudgetChatMessageType.QUERY_ERROR;
          const isResponse =
            message.type === BudgetChatMessageType.RESPONSE ||
            message.type === BudgetChatMessageType.RESPONSE_ERROR;

          return (
            <motion.div
              ref={(el) => registerMessageRef(message.messageId, el)}
              key={`${message.messageId}_${message.type}`}
              initial={{
                opacity: 0,
                scale: 0.95,
                x: message.type === BudgetChatMessageType.LOADING ? "-100%" : 0,
              }}
              animate={{ opacity: 1, scale: 1, x: 0 }}
              exit={{ x: isQuery ? 0 : "-100%" }}
              transition={{
                delay: isQuery ? 0.2 : 0.3,
                duration: isQuery ? 0.3 : 0.15,
                type: "tween",
                ease: "easeOut",
              }}
            >
              {isQuery ? (
                <div className="flex justify-end">
                  <BudgetChatQuery
                    key={`${message.messageId}_query`}
                    query={message.content}
                  />
                </div>
              ) : isResponse ? (
                <BudgetChatResponse
                  key={`${message.messageId}_response`}
                  messageId={message.messageId}
                  response={message.content}
                  payloadTopics={message.topics}
                  onTopicSelected={onTopicSelected}
                  viewedTopics={viewedTopics}
                />
              ) : (
                <BudgetChatLoadingPlaceholder
                  key={`${message.messageId}_loading`}
                />
              )}
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

export default BudgetChat;
